/**
 * The thin lane under the plot that says what every stretch of the window
 * WAS, whatever its response time: answered, partly unclear, down, or not
 * seen at all.
 *
 * The line alone cannot say this. A failed check has no time, so it is a
 * break in the line, and so is a stretch nothing was checked in; the lane
 * paints the first red and leaves the second empty. Blocked and unknown
 * checks (a firewall, a check the engine could not finish) are grey, never
 * red: they are not evidence of an outage (`laneTone`).
 *
 * Plain divs over the plot area rather than recharts shapes: the runs are
 * already cut to the window (`laneSegments`), so each is one absolutely
 * placed span in percent of the plot width, which also keeps the lane from
 * re-laying out recharts' SVG.
 */
import type { LaneTone } from "../../../../lib/monitoring";
import type { T } from "../words";
import { LANE_BOTTOM, LANE_HEIGHT, PLOT_LEFT, PLOT_RIGHT } from "./layout";

export const LANE_FILL: Record<LaneTone, string> = {
  up: "color-mix(in oklab, var(--color-success) 55%, transparent)",
  partial: "var(--color-warning)",
  down: "var(--color-destructive)",
  blind: "color-mix(in oklab, var(--color-muted-foreground) 30%, transparent)",
};

const TONES: readonly LaneTone[] = ["up", "partial", "down", "blind"];

export function StatusLane({
  segments,
  from,
  span,
}: {
  segments: { from: number; to: number; tone: LaneTone }[];
  from: number;
  span: number;
}) {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute overflow-hidden rounded-full"
      style={{ left: PLOT_LEFT, right: PLOT_RIGHT, bottom: LANE_BOTTOM, height: LANE_HEIGHT }}
    >
      {segments.map((s) => (
        <span
          key={`${s.from}:${s.tone}`}
          className="absolute inset-y-0"
          style={{
            left: `${((s.from - from) / span) * 100}%`,
            // A single raw check in a week-wide window is under a pixel; it still has to show.
            width: `max(2px, ${((s.to - s.from) / span) * 100}%)`,
            background: LANE_FILL[s.tone],
          }}
        />
      ))}
    </div>
  );
}

/** What the lane's colours mean. */
export function LaneLegend({ t }: { t: T }) {
  return (
    <div className="crm-mut flex flex-wrap gap-x-4 gap-y-1 text-[11.5px]">
      {TONES.map((tone) => (
        <span key={tone} className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2 w-3.5 rounded-full" style={{ background: LANE_FILL[tone] }} />
          {t(`monitoring.chart.lane.${tone}`)}
        </span>
      ))}
    </div>
  );
}
